import React, { useState } from 'react';
import { DailyPerformanceChart, MetricTogglePanel, TrinomicLossOverlay } from '../components/InverterTrendAnalysis';

const InverterDashboard: React.FC = () => {
  const [visibleMetrics, setVisibleMetrics] = useState(['production', 'yield', 'pr']);
  const [showTrinomicLoss, setShowTrinomicLoss] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateRange, setDateRange] = useState('');

  const inverterId = 'BLOCK-G1-KR1';
  const plantId = 'PSPR_Indulga';

  const handleToggleMetric = (metric: string) => {
    setVisibleMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const endDate = new Date(value);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 9); // 10 days range
      
      const formatDate = (date: Date) => date.toISOString().split('T')[0];
      setDateRange(`${formatDate(startDate)}_to_${formatDate(endDate)}`);
    } else {
      setDateRange('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Inverter PR & Yield Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                Daily Inverter Performance & PR Modeling - {inverterId} (IN-01)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range (End Date)
                </label>
                <input
                  type="date"
                  onChange={handleDateRangeChange}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                onClick={() => setShowTrinomicLoss(!showTrinomicLoss)}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  showTrinomicLoss 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {showTrinomicLoss ? 'Hide' : 'Show'} Trinomic Loss
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <MetricTogglePanel
              visibleMetrics={visibleMetrics}
              onToggleMetric={handleToggleMetric}
            />
          </div>
          
          {showTrinomicLoss && (
            <div>
              <TrinomicLossOverlay
                inverterId={inverterId}
                date={selectedDate}
                isVisible={showTrinomicLoss}
              />
            </div>
          )}
        </div>

        {/* Main Chart */}
        <DailyPerformanceChart
          inverterId={inverterId}
          dateRange={dateRange}
          plantId={plantId}
          visibleMetrics={visibleMetrics}
        />

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-600">Inverter</h4>
            <p className="text-lg font-semibold text-gray-900">{inverterId}</p>
            <p className="text-xs text-gray-500">IN-01</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-600">Available Metrics</h4>
            <p className="text-lg font-semibold text-blue-600">{visibleMetrics.length}</p>
            <p className="text-xs text-gray-500">of 7 total</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-600">Date Range</h4>
            <p className="text-lg font-semibold text-green-600">Oct 1-10</p>
            <p className="text-xs text-gray-500">10 days</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-600">Plant</h4>
            <p className="text-lg font-semibold text-purple-600">{plantId}</p>
            <p className="text-xs text-gray-500">Active</p>
          </div>
        </div>

        {/* Standards Compliance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Standards Alignment</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm">IEC 61724 - PR Calculation</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm">IEEE 1547 - Grid Integration</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm">IEC 62446 - Performance Monitoring</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InverterDashboard;